import { useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { formatMoney, money } from '@zibanu/shared';
import { cx } from './cx';
import { Icon } from './basics';
import type { IconName } from './icons';

export type MoneyDirection = 'in' | 'out' | 'owed' | 'neutral';

const MINUS = '−';

/**
 * Kobo on screen: "₦45,000", "+₦12,500", "−₦3,200" (a true minus). Never a
 * float in, never red for spending (design system: money has three colours).
 */
export function formatAmount(minor: number, opts: { direction?: MoneyDirection; sign?: boolean; currency?: string } = {}): string {
  const body = formatMoney(money(Math.abs(minor), opts.currency ?? 'NGN'));
  const sign = opts.sign
    ? opts.direction === 'in'
      ? '+'
      : opts.direction === 'out' || minor < 0
        ? MINUS
        : ''
    : minor < 0
      ? MINUS
      : '';
  return `${sign}${body}`;
}

export interface AmountProps {
  /** Kobo. */
  minor: number;
  direction?: MoneyDirection;
  size?: 'sm' | 'md' | 'lg' | 'hero';
  showSign?: boolean;
  currency?: string;
  className?: string;
}

const SAID: Record<MoneyDirection, string> = { in: 'Money in', out: 'Money out', owed: 'Owed', neutral: '' };

export function Amount({ minor, direction = 'neutral', size = 'md', showSign = true, currency, className }: AmountProps) {
  const text = formatAmount(minor, {
    direction,
    sign: showSign && direction !== 'neutral' && direction !== 'owed',
    ...(currency ? { currency } : {}),
  });
  return (
    <span className={cx('zb-amount', `zb-amount--${size}`, `zb-amount--${direction}`, className)}>
      {SAID[direction] && <span className="zb-sr">{`${SAID[direction]} `}</span>}
      {text}
    </span>
  );
}

export interface StatCardProps {
  label: string;
  /** Kobo. */
  minor: number;
  direction?: MoneyDirection;
  hero?: boolean;
  note?: ReactNode;
  icon?: IconName;
  className?: string;
}

/** A headline figure. `hero` paints it palm — once, on Home. */
export function StatCard({ label, minor, direction = 'neutral', hero, note, icon, className }: StatCardProps) {
  return (
    <div className={cx('zb-stat', hero && 'zb-stat--hero', className)}>
      <div className="zb-stat__label">
        {icon && <Icon name={icon} size={20} />}
        {label}
      </div>
      <Amount minor={minor} direction={direction} size={hero ? 'hero' : 'lg'} showSign={false} />
      {note && <div className="zb-stat__note">{note}</div>}
    </div>
  );
}

export interface AmountInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  hint?: string;
  error?: string | undefined;
}

/** The big naira entry: 40px figures behind a ₦, the numeric keypad on a phone. What's typed is parsed with parseNaira. */
export function AmountInput({ label = 'Amount', hint, error, id, className, ...rest }: AmountInputProps) {
  const made = useId();
  const inputId = id ?? made;
  const described = hint || error ? `${inputId}-d` : undefined;
  return (
    <div className={cx('zb-field', 'zb-amountin', error && 'zb-field--error', className)}>
      <label htmlFor={inputId} className="zb-field__label">
        {label}
      </label>
      <div className="zb-field__box">
        <span className="zb-amountin__cur" aria-hidden="true">
          ₦
        </span>
        <input
          id={inputId}
          className="zb-field__input"
          inputMode="decimal"
          autoComplete="off"
          placeholder="0"
          aria-invalid={error ? true : undefined}
          aria-describedby={described}
          {...rest}
        />
      </div>
      {(error || hint) && (
        <div id={described} className="zb-field__hint">
          {error && <Icon name="triangle-alert" size={16} />}
          {error || hint}
        </div>
      )}
    </div>
  );
}
